import { Entity, World } from "ecsy";
import { Composition, CompositionElement } from "./composition";
import { getEntityById, getId } from "./entityFactory";
import { GameElement } from "./gameElementTypes";
import { gainExperience, Player } from "./player";
import './debugOverrides'
import { VirusGrowthMessage } from "./messageTypes";
import { vec2 } from "@tlaukkan/tsm";
import { Spawner, VirusLoadIncrease, Cooldown } from "./components";
import { Multiplayer, SimulationConstants, VirusSpawnerConstants } from "./constants";

export type GameState = 'shop' | 'simulation' | 'lobby' | 'recap' | 'end' | 'reconnecting'; //TODO: Enum

export type RoundReward = {
	experience: number,
}

export enum GameMode {
	Solo,
	Asynchronous,
	LivePeerToPeer,
}

export type WaveInfo = { cells: CompositionElement[], type: GameElement, center: vec2, lane: number }
export type LaneInfo = { lane: number, center: vec2, cells: CompositionElement[] }

export abstract class Game {
	abstract world: World;

	isLiveMultiplayerGame() {
		return this.players.filter(player => !player.isBot).length > 1;
	}

	autosaveName() {
		return this.isLiveMultiplayerGame() ? Multiplayer.MultiplayerAutosaveName : Multiplayer.AutosaveName;
	}

	compIdMap = new Map<number, CompositionElement>();
	players = new Array<Player>();
	inSimulation: boolean = false;

	//This batch gets set once, and need to be restored when reloadiing the game
	latestVirusGrowth: VirusGrowthMessage[] = [];
	requiredPlayers = 1;
	roundReward: RoundReward | null = null;
	endlessMode = false;
	stats = {
		cellsCombined: 0,
		cellsTripled: 0,
		rewardsBought: 0,
	}
	roundStats: {
		[key: number]: {
			hpGained: number,
			electrictyVirusKills: number,
			maxSingleExplosionKills: number,
			immuneCellsSpawnedInCombat: number,
		}
	} = {};

	numberOfViruses = 0;
	numberOfBloodCells = 0;
	lastRoundSurvivors = new Array<number>();

	waveInfoPerPlayer: Array<WaveInfo>[] = [];
	laneInfoPerPlayer: Array<LaneInfo>[] = [];

	lostCellsPerPlayerThisRound = new Map<number, number>();

	abstract createInfectedCell(player: Player, virusType: GameElement): void;
	abstract getCurrentState(): GameState;
	abstract onCombine(player: Player, element: CompositionElement): void;
	abstract onPromote(player: Player, element: CompositionElement): void;
	abstract getCurrentRound(): number

	reset() {
		this.compIdMap = new Map();
		this.players = [];

		this.requiredPlayers = 0;
		this.numberOfBloodCells = 0;
		this.numberOfViruses = 0;
		this.lastRoundSurvivors = [];

		this.stats = {
			cellsCombined: 0,
			cellsTripled: 0,
			rewardsBought: 0,
		}

		this.waveInfoPerPlayer = [];
		this.lostCellsPerPlayerThisRound = new Map();
		this.endlessMode = false;
	}

	addCompositionElement(composition: Composition, element: CompositionElement) {
		composition.push(element);
		this.compIdMap.set(element.id, element);
	}

	removeCompositionElement(composition: Composition, id: number) {
		const index = composition.findIndex(el => el.id === id);
		composition.splice(index, 1);
		this.compIdMap.delete(id);
	}

	getCompElementById(id: number) {
		return this.compIdMap.get(id) || null;
	}

	getEntityCompElement(entity: Entity) {
		const id = getId(entity);
		if (id === null) return null;
		return this.getCompElementById(id);
	}

	getVirusWaves(player: Player) {
		if (this.waveInfoPerPlayer[player.id]) {
			return this.waveInfoPerPlayer[player.id];
		}
		else return [];
	}

	getVirusLanes(player: Player) {
		if (this.laneInfoPerPlayer[player.id]) {
			return this.laneInfoPerPlayer[player.id];
		}
		else return [];
	}

	//1. non-tiebreaker first wave cells don't get submerged
	//2. If all the viruses in the first wave die we have to wait now for the timeout before the next wave activates.

	totalWaves = Infinity; //TODO HACK
	nextVirusLane(playerId: number, waves: number[]) {
		const player = this.players.find(p => p.id === playerId);
		if (!player) throw new Error(`NextLaneSetup: Player with id ${playerId} not found`);
		const waveInfo = this.getVirusWaves(player);
		for (let waveIndex = 0; waveIndex < waves.length; waveIndex++) {
			const wave = waves[waveIndex];
			const thisWave = waveInfo[wave];
			this.totalWaves = waveInfo.length; //HACK
			for (const cell of thisWave.cells) {
				const entity = getEntityById(cell.id);
				if (entity) {
					//enable the spawner, which starts off "disabled" with an infinite cooldown
					const cooldown = entity.getComponent(Cooldown);
					if (waveIndex === 0) { //first wave in this lane's activation batch
						cooldown.extra = wave === 0 ? SimulationConstants.FirstWaveDelay : SimulationConstants.BetweenLaneDelay;
					}
					else { //follower waves for this lane/player, spawn after a short delay
						cooldown.extra = SimulationConstants.BetweenWaveDelay * waveIndex;
					}
				}
			}
		}
	}

	onGainMoney(player: Player, amount: number) {
	}

	loadGameState(newData: Player[]) {
		this.players = newData;
		this.regenearteCompIdMap();
		this.recomputeWaveInfo();
	}

	regenearteCompIdMap() {
		this.compIdMap = new Map();
		for (const player of this.players) {
			[player.immuneCells, player.enemyCells, player.hpCells, player.infectedCells].forEach(comp => {
				for (const element of comp) {
					this.compIdMap.set(element.id, element);
				}
			})
		}
	}

	recomputeWaveInfo() {
		this.waveInfoPerPlayer = [];
		this.laneInfoPerPlayer = [];

		for (const player of this.players) {
			if (!this.waveInfoPerPlayer[player.id]) this.waveInfoPerPlayer[player.id] = [];
			if (!this.laneInfoPerPlayer[player.id]) this.laneInfoPerPlayer[player.id] = [];

			let allEnemyCells = player.enemyCells;
			if (player.sharedLanes.length > 0) {
				for (const sharedLane of player.sharedLanes) {
					const sharedWith = this.players.find(otherPlayer => otherPlayer.id === sharedLane.otherPlayerId);
					if (!sharedWith) throw new Error(`Shared lane with non existing player ${sharedLane.otherPlayerId}`);
					const sharedCells = sharedWith.enemyCells.filter(cell => cell.entityParameters?.spawnLane === sharedLane.lane);
					allEnemyCells = allEnemyCells.concat(sharedCells);
				}
			}

			const laneCenters = Array.from({ length: VirusSpawnerConstants.LaneCount }, () => new vec2());
			const cellsInLane = Array.from({ length: VirusSpawnerConstants.LaneCount }, () => new Array<CompositionElement>());
			const wavesInLane = new Array<number>(VirusSpawnerConstants.LaneCount).fill(0);

			for (const cell of allEnemyCells) {
				const waveNum = cell.entityParameters!.spawnWave!;
				const waveType = cell.entityParameters?.spawnTargetType!;
				const center = cell.entityParameters!.spawnWaveCenter!;
				const lane = cell.entityParameters!.spawnLane!;

				if (!this.waveInfoPerPlayer[player.id][waveNum]) {
					this.waveInfoPerPlayer[player.id][waveNum] = {
						cells: [],
						type: waveType,
						center: new vec2(center),
						lane,
					};
				}
				this.waveInfoPerPlayer[player.id][waveNum].cells.push(cell);

				laneCenters[lane].add(new vec2(center));
				wavesInLane[lane]++;
				cellsInLane[lane].push(cell);
			}

			for (let lane = 0; lane < VirusSpawnerConstants.LaneCount; lane++) {
				this.laneInfoPerPlayer[player.id][lane] = {
					lane,
					center: laneCenters[lane].scale(1 / wavesInLane[lane]),
					cells: cellsInLane[lane]
				};
			}
		}
	}

	updateBattleRoundReward() {
		const isBattleRound = this.latestVirusGrowth.some(growth => growth.damageRequired > 0)
		this.roundReward = isBattleRound ? { experience: 1 } : null;
	}

	claimBattleRoundReward(winner: Player) {
		gainExperience(winner, 1);
		console.log(`[${getStepNumber()}]` + 'EXPERIENCE GAIN player ' + winner.id);
	}

	applyExistingEntityTiebreakerGrowth(cell: CompositionElement) {
		const entity = getEntityById(cell.id);
		if (entity) {
			const spawner = entity.getComponent(Spawner);
			spawner.amount += 1;
			entity.addComponent(VirusLoadIncrease);
		}
	}

	getRoundStat(playerId: number, stat: keyof typeof this.roundStats[number]): number {
		const stats = this.roundStats[playerId];
		if (stats === undefined) {
			return 0;
		}
		return stats[stat] || 0;
	}

	setRoundStat(playerId: number, stat: keyof typeof this.roundStats[number], value: number) {
		const stats = this.roundStats[playerId];
		if (stats === undefined) {
			// console.log('Creating new stats object for player: ' + playerId);
			this.roundStats[playerId] = {
				hpGained: 0,
				electrictyVirusKills: 0,
				maxSingleExplosionKills: 0,
				immuneCellsSpawnedInCombat: 0,
			};
		}
		this.roundStats[playerId][stat] = value;
	}

	incrementRoundStat(playerId: number, stat: keyof typeof this.roundStats[number], value: number) {
		const current = this.getRoundStat(playerId, stat);
		this.setRoundStat(playerId, stat, current + value);
	}

	clearRoundStats() {
		this.roundStats = {};
		for (const player of this.players) {
			this.roundStats[player.id] = {
				hpGained: 0,
				electrictyVirusKills: 0,
				maxSingleExplosionKills: 0,
				immuneCellsSpawnedInCombat: 0,
			};
		}
	}
}
